import {
  AnalyticsEventName,
  DUPE_GET_PAID_MODAL_COOKIE_KEY,
  FeatureFlags,
} from '@dupe/common/constants'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import React, { useCallback, useEffect, useState } from 'react'
import { BrowserView, MobileView } from 'react-device-detect'
import AnimatedBlurContainer from '~/src/components/assistant/AnimatedBlurContainer'
import { Dialog, DialogContent } from '~/src/components/ui/dialog'
import { Drawer, DrawerContent } from '~/src/components/ui/drawer'
import { useSearchContext } from '~/src/context/SearchProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthUser from '~/src/hooks/useAuthUser'
import useDismissible from '~/src/hooks/useDismissible'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'

const MAX_AGE_ONE_DAY = 60 * 60 * 24 // 1 day
const MAX_AGE_ONE_YEAR = 60 * 60 * 24 * 365 // 1 year

const GetPaidModal: React.FC = () => {
  const { authUser } = useAuthUser()
  const analytics = useAnalytics()
  const [isOpen, setIsOpen] = useState(false)
  const searchParams = useSearchParams()
  const shouldShowModal = searchParams?.get('test-modal') !== 'false'

  const { hasInteracted } = useSearchContext()

  const { isDismissed, dismiss } = useDismissible(
    DUPE_GET_PAID_MODAL_COOKIE_KEY,
    false
  )

  const { featureOn: rewardsProgramFeatureEnabled } = useIsFeatureOn(
    FeatureFlags.RewardsProgram,
    true
  )

  const dismissCallback = useCallback(
    (open?: boolean) => {
      dismiss({
        maxAge: MAX_AGE_ONE_DAY,
      })
      setIsOpen(open ?? false)
    },
    [dismiss, setIsOpen]
  )

  const activateCallback = useCallback(() => {
    dismiss({
      maxAge: authUser?.uid ? MAX_AGE_ONE_YEAR : MAX_AGE_ONE_DAY,
    })
    setIsOpen(false)
  }, [authUser?.uid, dismiss, setIsOpen])

  // show modal after 3 seconds, unless dismissed
  // or user has interacted with the input
  // or the feature is disabled
  // or modal=false in URL.
  useEffect(() => {
    if (hasInteracted) return
    if (isDismissed) return
    if (isOpen) return
    if (!rewardsProgramFeatureEnabled) return
    if (!shouldShowModal) return

    const timer = setTimeout(() => {
      setIsOpen(true)
      analytics.track(AnalyticsEventName.GetPaidModalVisible)
    }, 3000)

    return () => clearTimeout(timer)
    // The `analytics` object causes this useEffect to
    // run multiple times, so we exclude it from the dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDismissed,
    isOpen,
    rewardsProgramFeatureEnabled,
    hasInteracted,
    shouldShowModal,
  ])

  if (hasInteracted) return null
  if (isDismissed) return null
  if (!isOpen) return null
  if (!rewardsProgramFeatureEnabled) return null
  if (!shouldShowModal) return null

  return (
    <>
      <MobileView>
        <Drawer open={isOpen} onOpenChange={dismissCallback}>
          <DrawerContent
            className='bg-black text-white'
            overlayClassName='bg-black/10'
            notchClassName='bg-black'
          >
            <GetPaidModalContent
              dismissCallback={dismissCallback}
              activateCallback={activateCallback}
              showSkip
            />
          </DrawerContent>
        </Drawer>
      </MobileView>
      <BrowserView>
        <Dialog open={isOpen} onOpenChange={dismissCallback}>
          <DialogContent
            className='m-0 max-w-screen-md overflow-hidden rounded-[20px] border-none bg-transparent p-0 xl:max-w-screen-md'
            closeClassName='text-white'
            overlayClassName='bg-black/20'
            adjustForSidebar
          >
            <GetPaidModalContent
              dismissCallback={dismissCallback}
              activateCallback={activateCallback}
            />
          </DialogContent>
        </Dialog>
      </BrowserView>
    </>
  )
}

interface GetPaidModalContentProps {
  dismissCallback?: () => void
  activateCallback?: () => void
  showSkip?: boolean
}

const GetPaidModalContent: React.FC<GetPaidModalContentProps> = ({
  dismissCallback,
  activateCallback,
  showSkip,
}) => {
  const analytics = useAnalytics()
  const { authUser } = useAuthUser()
  const href = authUser ? '/$dupe/rewards' : '/signup?redirect=/$dupe/rewards'

  return (
    <div className='flex flex-col items-center justify-center bg-black px-10 py-16 text-white'>
      <div className='pointer-events-none absolute inset-x-0 bottom-0 h-[340px] w-full bg-[url("/assets/rewards/dupe-darkcoins.png")] bg-cover bg-center md:bg-contain'>
        <div className='absolute inset-x-0 -top-10 h-[100px] bg-gradient-to-b from-black to-transparent' />
      </div>
      <div className='relative flex flex-col items-center justify-center gap-y-6 pb-[200px]'>
        <h1 className='max-w-md text-center text-[42px]/[45px] font-bold md:text-[45px]/[50px]'>
          Get paid every time you use Dupe
        </h1>
        <p className='mx-auto max-w-md text-center text-base/snug opacity-70'>
          Earn when you run searches, or invite friends! Redeem for cash or huge
          discounts.
        </p>

        <AnimatedBlurContainer
          borderClassName='rounded-full'
          glow='hover'
          borderWidth={3}
        >
          <Link
            href={href}
            className='bg-dupe block rounded-full px-10 py-4 font-semibold text-black transition hover:bg-white md:text-lg'
            onClick={() => {
              if (activateCallback) {
                activateCallback()
              }

              analytics.track(AnalyticsEventName.GetPaidModalClick)
            }}
          >
            Activate $DUPE Points
          </Link>
        </AnimatedBlurContainer>

        {showSkip && (
          <button
            className='text-sm opacity-60'
            onClick={dismissCallback}
            type='button'
          >
            Not now
          </button>
        )}
      </div>
    </div>
  )
}

export default GetPaidModal
